import { useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';
import type { TranslationKeys } from '../../i18n/types.js';
import { Note } from '../ui/card';
import { FIELD_LABEL_CLASS } from '../ui/section-header';
import { Pill } from '../ui/pill';
import { IconButton } from '../ui/icon-button';
import { RowMenu } from '../ui/row-menu';
import { FileText, MapPin, Share2 } from 'lucide-react';
import { TiaMarkdown } from '../ai/TiaMarkdown.js';
import { PaneSection } from './PaneParts.js';
import { AddLink } from '../ui/add-link';
import { InlineNoteForm, type NoteDraft } from './InlineNoteForm.js';

/** A note of the beat as its card shows it (mockup gm-studio-quests-preview.html noteHtml()). */
export interface BeatNoteView {
  readonly id: string;
  readonly name: string;
  /** «Видят игроки» — what the players get when the note is shown. */
  readonly playersText: string;
  /** «Для мастера» — never leaves the GM. */
  readonly gmText: string;
  /** Where the note lives, when it is not the beat itself: «в цели «…»». */
  readonly ownerLabel?: string;
  /** The note's pin on a map, if it has one. */
  readonly pin?: { readonly id: string; readonly pageName: string; readonly shown: boolean };
  /** Lives in the shared list: no «Make shared». */
  readonly shared: boolean;
}

export interface BeatNotesProps {
  /** The notes that live in this beat (not its prep text). */
  readonly notes: readonly BeatNoteView[];
  readonly t: (key: keyof TranslationKeys) => string;
  readonly onOpen: (entryId: string) => void;
  /** «＋ заметка в бит» → the form → «Готово»: a new note that lives in this beat is made from what was typed. */
  readonly onCreate: (draft: NoteDraft) => void;
  /** «Поставить на карту»: pick the place on the map beside. */
  readonly onPlace: (entryId: string) => void;
  /** «Сделать общей». */
  readonly onShare: (entryId: string) => void;
  /** The badge «на карте · …»: light its pin on the map. */
  readonly onFlashPin: (entryId: string) => void;
  /** The pointer is over a card (or left it): its pin is lit on the map beside. */
  readonly onHoverNote: (entryId: string | null) => void;
  /** A pin of this note was clicked on the map: the card is scrolled to and lit. */
  readonly highlightedId?: string | undefined;
  /** AU-8: notes the AI made and the GM has not seen yet — lit (`.ai-new`). */
  readonly litIds?: ReadonlySet<string> | undefined;
}

const who = FIELD_LABEL_CLASS;

export interface NoteCardProps {
  readonly note: BeatNoteView;
  readonly t: (key: keyof TranslationKeys) => string;
  /** Under an objective (mockup `.note.inner`): the contained, left-ruled card. */
  readonly inner?: boolean;
  readonly cardRef?: (el: HTMLDivElement | null) => void;
  readonly onOpen: (entryId: string) => void;
  readonly onPlace: (entryId: string) => void;
  readonly onShare: (entryId: string) => void;
  readonly onFlashPin: (entryId: string) => void;
  readonly onHoverNote: (entryId: string | null) => void;
  readonly highlightedId?: string | undefined;
  readonly litIds?: ReadonlySet<string> | undefined;
}

/** One note's card — in the beat's «Notes» and (inner) under its objective. */
export function NoteCard({ note, t, inner = false, cardRef, onOpen, onPlace, onShare, onFlashPin, onHoverNote, highlightedId, litIds }: NoteCardProps): JSX.Element {
  return (
      <Note
        ref={cardRef}
        inner={inner}
        className={`${inner ? 'max-w-[72ch]' : ''}${highlightedId === note.id ? ' ring-2 ring-hl' : litIds?.has(note.id) ? ' ring-1 ring-inset ring-ai/55 bg-ai/10' : ''}`}
        data-ai-new={litIds?.has(note.id) ? 'true' : undefined}
        data-testid={inner ? 'objective-note' : 'beat-note'}
        data-entry-id={note.id}
        data-highlighted={highlightedId === note.id ? 'true' : undefined}
        draggable="true"
        title={t('studio.note.dragTitle')}
        onMouseEnter={(): void => onHoverNote(note.id)}
        onMouseLeave={(): void => onHoverNote(null)}
        onDragStart={(e): void => {
          e.dataTransfer.setData('text/plain', note.name);
          e.dataTransfer.setData('application/json', JSON.stringify({ dragType: 'note-entry', entryId: note.id, entryName: note.name }));
          e.dataTransfer.effectAllowed = 'copyMove';
        }}
      >
        {/* The head line (group T): the name opens the note; its pills; «Поставить на карту» as an icon and ⋯ with every
            action in words (П3). */}
        <div className="flex min-h-7 flex-wrap items-center gap-1.5">
          <button
            type="button"
            className="min-w-0 text-left text-ui font-semibold text-fg hover:underline"
            data-testid="beat-note-open"
            onClick={(): void => onOpen(note.id)}
          >
            {note.name}
          </button>
          {note.ownerLabel !== undefined ? (
            <Pill tone="own">{note.ownerLabel}</Pill>
          ) : null}
          {note.pin !== undefined ? (
            <button
              type="button"
              className={`rounded-full px-2 py-px text-pill ${note.pin.shown ? 'bg-ok/13 text-ok' : 'border border-line text-muted-foreground'}`}
              title={t('studio.note.pinTitle')}
              onClick={(): void => onFlashPin(note.id)}
            >
              {t(note.pin.shown ? 'studio.note.onMapShown' : 'studio.note.onMapGm')}
            </button>
          ) : null}
          <span className="ml-auto flex flex-none items-center gap-0.5">
            {note.pin === undefined ? (
              <IconButton
                size="sm"
                label={t('studio.note.placeOnMap')}
                icon={<MapPin size={14} aria-hidden="true" />}
                data-testid="beat-note-place"
                onClick={(): void => onPlace(note.id)}
              />
            ) : null}
            <RowMenu
              testid="beat-note-menu"
              items={[
                { label: t('studio.note.open'), icon: FileText, onSelect: () => onOpen(note.id) },
                { label: t('studio.note.placeOnMap'), icon: MapPin, hidden: note.pin !== undefined, onSelect: () => onPlace(note.id) },
                { label: t('studio.note.makeShared'), icon: Share2, hidden: note.shared, testid: 'beat-note-share', onSelect: () => onShare(note.id) },
              ]}
            />
          </span>
        </div>
        {note.playersText !== '' ? (
          <div>
            <div className={who}>{t('studio.note.playersSee')}</div>
            {/* The text is Markdown (the editor stores it so): shown as the reader shows it. */}
            <TiaMarkdown text={note.playersText} className="text-body-s text-fg" />
          </div>
        ) : null}
        {note.gmText !== '' ? (
          <div>
            <div className={who}>{t('studio.note.forGm')}</div>
            <TiaMarkdown text={note.gmText} className="text-body-s text-muted-foreground" />
          </div>
        ) : null}
        {note.pin !== undefined ? <span className="text-meta text-dim">{t('studio.note.pinOn').replace('{map}', note.pin.pageName)}</span> : null}
      </Note>
  );
}

/** «Заметки бита» (P03, P09b Q-3/Q-7/Q-8): the cards of what the GM keeps in this beat; each is dragged onto a map as a pin. */
export function BeatNotes({ notes, t, onOpen, onCreate, onPlace, onShare, onFlashPin, onHoverNote, highlightedId, litIds }: BeatNotesProps): JSX.Element {
  const refs = useRef(new Map<string, HTMLDivElement>());
  const [adding, setAdding] = useState(false);
  useEffect(() => {
    if (highlightedId === undefined) return;
    refs.current.get(highlightedId)?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  }, [highlightedId]);

  return (
    <PaneSection title={t('studio.owner.beatNotes')} testid="beat-notes">
      {notes.length > 0 ? (
        <ul className="mb-1.5 flex flex-col gap-1.5">
          {notes.map((note) => (
            <li key={note.id}>
              <NoteCard
                note={note}
                t={t}
                cardRef={(el): void => {
                  if (el === null) refs.current.delete(note.id);
                  else refs.current.set(note.id, el);
                }}
                onOpen={onOpen}
                onPlace={onPlace}
                onShare={onShare}
                onFlashPin={onFlashPin}
                onHoverNote={onHoverNote}
                highlightedId={highlightedId}
                litIds={litIds}
              />
            </li>
          ))}
        </ul>
      ) : null}
      {adding ? (
        <InlineNoteForm
          t={t}
          onCancel={(): void => setAdding(false)}
          onSubmit={(draft): void => {
            setAdding(false);
            onCreate(draft);
          }}
        />
      ) : (
        <AddLink plus={false} data-testid="beat-note-add" onClick={(): void => setAdding(true)}>
          {t('studio.owner.noteInBeat')}
        </AddLink>
      )}
    </PaneSection>
  );
}
